import { TableOutlined } from "@ant-design/icons";
import { Button, Popover, Segmented, Space, Typography } from "antd";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_TABLE_DISPLAY,
  type TableColumnWidth,
  type TableDisplayPreferences,
  type TableRowHeight,
} from "./tableDisplayPreferences";

export function TableDisplayControl({
  value,
  onChange,
}: {
  value: TableDisplayPreferences;
  onChange: (next: TableDisplayPreferences) => void;
}) {
  const { t } = useTranslation();
  const label = t("common.tableDisplay", "Table display");
  const optionLabel = (key: string, fallback: string) => t(`common.${key}`, fallback);
  const content = <div className="table-display-control" role="group" aria-label={label}>
    <div className="table-display-control__field">
      <Typography.Text type="secondary">{t("common.columnWidth", "Column width")}</Typography.Text>
      <Segmented
        aria-label={t("common.columnWidth", "Column width")}
        block
        value={value.columnWidth}
        options={[
          { value: "compact", label: optionLabel("compact", "Compact") },
          { value: "standard", label: optionLabel("standard", "Standard") },
          { value: "wide", label: optionLabel("wide", "Wide") },
        ]}
        onChange={(columnWidth) => onChange({ ...value, columnWidth: columnWidth as TableColumnWidth })}
      />
    </div>
    <div className="table-display-control__field">
      <Typography.Text type="secondary">{t("common.rowHeight", "Row height")}</Typography.Text>
      <Segmented
        aria-label={t("common.rowHeight", "Row height")}
        block
        value={value.rowHeight}
        options={[
          { value: "compact", label: optionLabel("compact", "Compact") },
          { value: "standard", label: optionLabel("standard", "Standard") },
          { value: "comfortable", label: optionLabel("comfortable", "Comfortable") },
        ]}
        onChange={(rowHeight) => onChange({ ...value, rowHeight: rowHeight as TableRowHeight })}
      />
    </div>
    <Space className="table-display-control__footer">
      <Typography.Text type="secondary">
        {t("common.tableDisplayHint", "Applies to every table and keeps wide data horizontally scrollable.")}
      </Typography.Text>
      <Button
        size="small"
        disabled={value.columnWidth === DEFAULT_TABLE_DISPLAY.columnWidth && value.rowHeight === DEFAULT_TABLE_DISPLAY.rowHeight}
        onClick={() => onChange({ ...DEFAULT_TABLE_DISPLAY })}
      >
        {t("common.reset", "Reset")}
      </Button>
    </Space>
  </div>;

  return <Popover content={content} placement="bottomRight" title={label} trigger="click">
    <Button size="small" icon={<TableOutlined />} aria-label={label} title={label} />
  </Popover>;
}
